import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import _ from 'lodash';
import { NetworkStatus } from 'apollo-boost';

import { Box, Text } from 'Components/Base';
import Breadcrumb from 'Components/Breadcrumb';
import Loading from 'Components/Loading';
import { getFilterData, convertToBreadCrumb } from 'Containers/AttendanceFilter';
import { getAdaptiveQuizIndividualFilterValue } from 'Containers/AdaptiveQuiz/utils';
import AttendanceFilterData from 'Domains/AttendanceFilterData';
import QuizFeedAttemptReportData from 'Domains/QuizFeedAttemptReportData';
import { SYLLABUS_MODE } from 'Domains/syllabus';

import AdaptiveQuizAttemptTable from './AdaptiveQuizAttemptTable';

const AdaptiveQuizAttemptTableWrapper = styled(Box)`
  position: relative;
  overflow: auto;
  & > div {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    overflow: visible;
  }
`;

const EmptyMessage = ({ message }) => (
  <AdaptiveQuizAttemptTable
    data={{ attempts: [] }}
    renderEmpty={({ colSpan }) => (
      <tr>
        <td colSpan={colSpan}>
          <Box bg={'tableRowBg'} py={9}>
            <Text style={{ textAlign: 'center' }}>{message}</Text>
          </Box>
        </td>
      </tr>
    )}
  />
);

const AdaptiveQuizAttemptTableView = ({
  data,
  translation,
  setDisabledFetch,
  onReportLoaded,
  mode,
}) => {
  const report = data?.foQuizFeedAttemptReport;
  const isSupported = mode === SYLLABUS_MODE.TUNGKAI && report?.isSupported !== false;

  useEffect(() => {
    setDisabledFetch(!isSupported);
  }, [isSupported, setDisabledFetch]);

  useEffect(() => {
    onReportLoaded(report ?? null);
  }, [report, onReportLoaded]);

  if (!isSupported) return <EmptyMessage message={translation('NOT_SUPPORTED')} />;
  if (_.isEmpty(report?.attempts)) {
    return <EmptyMessage message={translation('ATTEMPT_NOT_FOUND')} />;
  }
  return <AdaptiveQuizAttemptTable data={report} />;
};

const AdaptiveQuizIndividual = ({
  isFetchData,
  setIsFetchData,
  setDisabledFetch,
  onReportLoaded,
  schoolCode,
  username,
}) => {
  const { t } = useTranslation('adaptiveQuiz');
  const queryString = new URLSearchParams(useLocation().search);
  const [currentFilter, setCurrentFilter] = useState({});
  const [isFirstTime, setIsFirstTime] = useState(true);

  const filterValue = getAdaptiveQuizIndividualFilterValue(queryString);

  return (
    <>
      <Box>
        <AttendanceFilterData initialValue={filterValue} schoolCode={schoolCode}>
          {({ list, value }) => {
            const { termList, gradeList, classList, subjectList, syllabusList, lessonList } = list;
            if (
              isFirstTime &&
              termList &&
              gradeList &&
              classList &&
              subjectList &&
              syllabusList &&
              lessonList
            ) {
              const filterData = getFilterData(list, value);
              setCurrentFilter(
                filterValue.lessonName
                  ? { ...filterData, lesson: { sectionName: filterValue.lessonName } }
                  : filterData
              );
              setIsFirstTime(false);
            }

            return (
              <>
                <Text.Title>{t('STUDENT_INFO')}</Text.Title>
                <Breadcrumb
                  maxItemsPerRow={6}
                  items={convertToBreadCrumb(currentFilter)}
                  p={0}
                  my={2}
                />
              </>
            );
          }}
        </AttendanceFilterData>
      </Box>
      <AdaptiveQuizAttemptTableWrapper>
        <QuizFeedAttemptReportData value={filterValue} schoolCode={schoolCode} username={username}>
          {({ loading, error, data, refetch, networkStatus }) => {
            if (loading || networkStatus === NetworkStatus.refetch) return <Loading />;
            if (error) return null;
            if (isFetchData) {
              refetch();
              setIsFetchData(false);
            }
            return (
              <AdaptiveQuizAttemptTableView
                data={data}
                translation={t}
                setDisabledFetch={setDisabledFetch}
                onReportLoaded={onReportLoaded}
                mode={filterValue.mode}
              />
            );
          }}
        </QuizFeedAttemptReportData>
      </AdaptiveQuizAttemptTableWrapper>
    </>
  );
};

export default AdaptiveQuizIndividual;
